/**
 * Clean, simple scroll utility
 * Handles smooth scrolling to elements with proper offset
 */

/**
 * Scroll to a specific element or position
 * @param {HTMLElement|string|number} target - Element, element ID, or Y position
 * @param {Object} options - Scroll options
 * @param {number} options.offset - Offset from top (default: 100)
 * @param {string} options.behavior - Scroll behavior (default: 'smooth')
 * @returns {boolean} Success status
 */
export const scrollToTarget = (target, options = {}) => {
  const { offset = 100, behavior = 'smooth' } = options;

  let scrollPosition = 0;
  let targetElement = null;

  // Handle different target types
  if (typeof target === 'number') {
    // Direct position
    scrollPosition = target;
  } else if (typeof target === 'string') {
    // Element ID - try multiple methods
    targetElement = document.getElementById(target);
    if (!targetElement) {
      // Try with querySelector as fallback
      targetElement = document.querySelector(`#${target}`);
    }
    if (!targetElement) {
      // Try without the hash
      targetElement = document.querySelector(`[id="${target}"]`);
    }
    if (!targetElement) {
      // Element not found
      return false;
    }
    scrollPosition =
      targetElement.getBoundingClientRect().top + window.scrollY - offset;
  } else if (target instanceof HTMLElement) {
    // Direct element reference
    targetElement = target;
    scrollPosition =
      targetElement.getBoundingClientRect().top + window.scrollY - offset;
  } else {
    return false;
  }

  // Ensure position is not negative
  scrollPosition = Math.max(0, scrollPosition);

  // Perform the scroll
  try {
    window.scrollTo({
      top: scrollPosition,
      left: 0,
      behavior: behavior
    });
    return true;
  } catch (error) {
    // Fallback for older browsers
    try {
      window.scrollTo(0, scrollPosition);
      return true;
    } catch (fallbackError) {
      return false;
    }
  }
};

/**
 * Scroll to top of page
 * @param {string} behavior - Scroll behavior (default: 'auto')
 */
export const scrollToTop = (behavior = 'auto') => {
  scrollToTarget(0, { offset: 0, behavior });
};
